import { Dispatch, FC, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import { GapperParamType, GapperResponse, HotGappersTabEnum } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getGappers as getGappersCall } from '../../lib/store/fundamental';
import { DEFAULT_PAGE, DEFAULT_PAGE_LIST_SIZE } from '../../lib/store/fundamental/constants';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import MarketStateCache from '../../lib/store-util/MarketStateCache';
import { getLimitedText } from '../../lib/util/DataHelpers';
import { isCallStatusError, isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';
import { useMarketTimer } from '../../lib/util/hooks/MarketTimerHook';
import { isMarketRegularHours } from '../../lib/util/MarketDataHelpers';

import { useSubscriptionManager } from '../../hooks/useSubscriptionManager';
import HotGappers from '../../shared_components/HotGappers/HotGappers';
import { HOT_GAPPERS_SYMBOL_NAME_LIMIT } from '../../shared_components/HotGappers/HotGappers.constants';
import { GapperRow } from '../../shared_components/HotGappers/HotGappers.types';
import { UI_ROUTES } from '../../util/routes';

const MARKET_TIMER_MODE = 'discover-hot-gappers';

const HotGappersWrapper: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    isGappersReady,
    isGappersError,
    gappersData,
    companyLogoAndName,
    getGappers,
  } = props;
  const { marketState } = MarketStateCache.use();
  const isMarketHours = isMarketRegularHours(marketState);
  const { timerValueAsString, isEnabled: isTimerEnabled } = useMarketTimer(MARKET_TIMER_MODE);
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState<HotGappersTabEnum>(HotGappersTabEnum.GapUp);
  const isGapUpSelected = selectedTab === HotGappersTabEnum.GapUp;

  const gapperResponses: GapperResponse[] = gappersData.content || [];

  // Single market: the row's market fields are constants here, not resolved per symbol.
  const rows = useMemo((): GapperRow[] => (
    gapperResponses.map((item) => {
      const entry = companyLogoAndName[item.symbol];

      return {
        ...item,
        lookupSymbol: item.symbol,
        chartSymbol: item.symbol,
        isEuroStock: false,
        currencySymbol: CurrencySymbolEnum.USD,
        isEuropeanSymbol: false,
        logo: entry?.logo ?? '',
        url: entry?.url ?? item.url ?? '',
        displayName: getLimitedText(entry?.name ?? item.name, HOT_GAPPERS_SYMBOL_NAME_LIMIT),
      };
    })
  ), [gapperResponses, companyLogoAndName]);

  const symbolsToSubscribe = useMemo(() => (
    gapperResponses.map(item => ({
      symbol: item.symbol,
      stockExchangeCode: item.stockExchangeCode,
      countryDesignation: companyLogoAndName[item.symbol]?.countryDesignation,
    }))
  ), [gapperResponses, companyLogoAndName]);

  useSubscriptionManager({
    symbols: symbolsToSubscribe,
    enabled: isGappersReady && symbolsToSubscribe.length > 0,
  });

  useEffect(() => {
    if (!isMarketHours) {
      return;
    }

    getGappers({
      abovePreviousClose: isGapUpSelected,
      page: DEFAULT_PAGE,
      size: DEFAULT_PAGE_LIST_SIZE,
    } as GapperParamType);
  }, [selectedTab, isMarketHours]);// eslint-disable-line react-hooks/exhaustive-deps

  const handleSymbolClick = (symbol: string) => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`);
  };

  const handleViewAllClick = () => {
    navigate(`${UI_ROUTES.discover}${UI_ROUTES.gapTrackers}`, {
      state: { abovePreviousClose: isGapUpSelected },
    });
  };

  return (
    <HotGappers
      rows={rows}
      totalElements={gappersData.totalElements}
      isGapUpSelected={isGapUpSelected}
      isGapDownSelected={!isGapUpSelected}
      isMarketHours={isMarketHours}
      isReady={isGappersReady}
      isError={isGappersError}
      isTimerEnabled={isTimerEnabled}
      timerValueAsString={timerValueAsString}
      onTabChange={isGapUp => setSelectedTab(isGapUp ? HotGappersTabEnum.GapUp : HotGappersTabEnum.GapDown)}
      onSymbolClick={handleSymbolClick}
      onViewAllClick={handleViewAllClick}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  isGappersReady: isCallStatusReady(FundamentalCall.getGappers, state.fundamental.statusByCall),
  isGappersError: isCallStatusError(FundamentalCall.getGappers, state.fundamental.statusByCall),
  gappersData: state.fundamental.gappersData,
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getGappers: (params: GapperParamType) => dispatch(getGappersCall(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(HotGappersWrapper);
